import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { legalDocumentsApi, authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import type {
    ActiveRoleAssignmentResponse,
    LegalDocumentMutableStatus,
    LegalDocumentListItem,
} from '@anchorbase/pillar-sdk';
import { hasInternalAdminPrivileges } from '@anchorbase/pillar-sdk';
import { Status } from '@anchorbase/pillar-sdk';

export function LegalDocumentsListPage() {
    const log = createComponentLogger('LegalDocumentsListPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const [mutationError, setMutationError] = useState<string | null>(null);

    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });

    const canView: boolean = hasInternalAdminPrivileges(activeRoleAssignment ?? null);
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => ['legalDocuments', `canCreate=${canView}`],
        [canView]
    );
    const updateStatusMutation = useMutation({
        mutationFn: async (variables: {
            legalDocumentUuid: string;
            status: LegalDocumentMutableStatus;
        }) => {
            return await legalDocumentsApi.updateLegalDocumentStatus(variables.legalDocumentUuid, {
                status: variables.status,
            });
        },
        onSuccess: async () => {
            setMutationError(null);
            await queryClient.invalidateQueries({ queryKey: ['legalDocuments'] });
            await queryClient.invalidateQueries({ queryKey: ['legalDocument'] });
            await queryClient.invalidateQueries({ queryKey: ['pendingRequiredLegalDocuments'] });
        },
        onError: (error: unknown) => {
            log.error('Failed to update legal document status', error);
            setMutationError('Unable to update legal document status.');
        },
    });

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {mutationError === null ? null : (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {mutationError}
                </div>
            )}
            <InfiniteScrollList<LegalDocumentListItem, string, ReadonlyArray<string>>
                pageTitle="Legal Documents"
                queryPlaceholder="Search by title or version"
                queryKeyPrefix={queryKeyPrefix}
                pageSize={25}
                fetchPage={async (params) => {
                    const response = await legalDocumentsApi.searchLegalDocumentsPage({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                    return { items: response.legal_documents, pagination: response.pagination };
                }}
                parseCursorParam={(pageParam: unknown) => {
                    if (pageParam === null || pageParam === undefined) {
                        return undefined;
                    }
                    const parseResult = z.string().safeParse(pageParam);
                    if (!parseResult.success) {
                        throw new Error('Expected string cursor');
                    }
                    return parseResult.data;
                }}
                stateCopy={{
                    loadingLabel: 'Loading legal documents...',
                    emptyLabel: 'No legal documents found.',
                    endOfListLabel: 'End of legal documents.',
                    genericErrorLabel: 'Failed to load legal documents.',
                }}
                headerActions={
                    canView ? (
                        <Button
                            onClick={() => {
                                runInBackground(
                                    Promise.resolve(
                                        breadcrumbNavigate({
                                            source: 'panel',
                                            target: {
                                                url: '/legal-documents/create',
                                                title: 'Create Legal Document',
                                            },
                                        })
                                    ),
                                    (err: unknown) => {
                                        log.error('Navigation failed', err);
                                    }
                                );
                            }}
                        >
                            Create
                        </Button>
                    ) : undefined
                }
                renderItems={({ items }) => (
                    <div className="space-y-3">
                        {items.map((doc: LegalDocumentListItem) => {
                            return (
                                <div
                                    key={doc.legal_document_uuid}
                                    role="button"
                                    tabIndex={0}
                                    className="w-full cursor-pointer rounded-lg border bg-card text-left hover:bg-gray-50"
                                    onClick={() => {
                                        runInBackground(
                                            Promise.resolve(
                                                breadcrumbNavigate({
                                                    source: 'panel',
                                                    target: {
                                                        url: `/legal-documents/${doc.legal_document_uuid}`,
                                                        title: 'Legal Document Details',
                                                    },
                                                })
                                            ),
                                            (err: unknown) => {
                                                log.error('Navigation failed', err);
                                            }
                                        );
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key !== 'Enter' && event.key !== ' ') {
                                            return;
                                        }
                                        event.preventDefault();
                                        runInBackground(
                                            Promise.resolve(
                                                breadcrumbNavigate({
                                                    source: 'panel',
                                                    target: {
                                                        url: `/legal-documents/${doc.legal_document_uuid}`,
                                                        title: 'Legal Document Details',
                                                    },
                                                })
                                            ),
                                            (err: unknown) => {
                                                log.error('Navigation failed', err);
                                            }
                                        );
                                    }}
                                >
                                    <div className="border-b p-6 pb-2">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="text-base font-semibold">
                                                {doc.legal_document_title} (
                                                {doc.legal_document_version})
                                            </div>
                                            <StatusBadge status={doc.status} />
                                        </div>
                                    </div>
                                    <div className="p-6 pt-4 text-sm text-gray-600">
                                        <div>
                                            In force:{' '}
                                            {new Date(doc.in_force_start).toLocaleString()} -{' '}
                                            {doc.in_force_end === null
                                                ? 'No end date'
                                                : new Date(doc.in_force_end).toLocaleString()}
                                        </div>
                                        <div>
                                            Required: users={String(doc.required_for_users)} orgs=
                                            {String(doc.required_for_orgs)}
                                        </div>
                                        {canView ? (
                                            <div className="mt-4 flex gap-2">
                                                {doc.status === Status.ACTIVE ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        disabled={updateStatusMutation.isPending}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setMutationError(null);
                                                            updateStatusMutation.mutate({
                                                                legalDocumentUuid:
                                                                    doc.legal_document_uuid,
                                                                status: Status.DISABLED,
                                                            });
                                                        }}
                                                    >
                                                        Disable
                                                    </Button>
                                                ) : null}
                                                {doc.status === Status.PENDING ||
                                                doc.status === Status.DISABLED ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        disabled={updateStatusMutation.isPending}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setMutationError(null);
                                                            updateStatusMutation.mutate({
                                                                legalDocumentUuid:
                                                                    doc.legal_document_uuid,
                                                                status: Status.ACTIVE,
                                                            });
                                                        }}
                                                    >
                                                        {doc.status === Status.PENDING
                                                            ? 'Activate'
                                                            : 'Re-enable'}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            />
        </div>
    );
}
